import { useState, useEffect, useRef, memo } from "react";
import TransmitterFaceplate from "./TransmitterFaceplate";
import { useTrend } from "../context/TrendContext";
import { saveTrendPoint } from "../database/historianDB";

function Transmitter({
  tag,
  value,
  x,
  y,

  unit = "",
  min = 0,
  max = 100,
  HH = 90,
  H = 75,
  L = 25,
  LL = 10,
  description = "",

  // VALUE TEXT COLOR
  c = "lime",

  // VALUE BACKGROUND
  valueBg = "#111",

  // TAG COLORS
  tagColor = "white",
  tagBg = "#003366",

  scale = 1,
  MinValue = 0.003,
}) {

  // ================= FACEPLATE =================
  const [showFaceplate, setShowFaceplate] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipLeft, setTooltipLeft] = useState(false);

  // ================= DISPLAY VALUE =================
  const [displayValue, setDisplayValue] = useState(() => {
    const saved = localStorage.getItem(`transmitter_${tag}`);
    const parsed = Number(saved);
    return !isNaN(parsed) && parsed > 0 ? parsed : MinValue;
  });

  const intervalRef = useRef(null);
  const isMountedRef = useRef(false);
  const targetValueRef = useRef(MinValue);
  
  const { logValue } = useTrend();
  const lastLogTimeRef = useRef(0);

  // ======================================================
  // UPDATE TARGET VALUE IN REF (doesn't trigger re-renders)
  // ======================================================
  useEffect(() => {
    const safeValue = Number(value);
    const target = !isNaN(safeValue) && safeValue !== null
      ? safeValue * scale
      : MinValue;
    
    targetValueRef.current = target;
  }, [value, scale, MinValue]);

  // ======================================================
  // HISTORIAN + TREND LOGGER
  // ======================================================
  useEffect(() => {
    const now = Date.now();
    if (now - lastLogTimeRef.current < 1000) return;
    lastLogTimeRef.current = now;

    if (!tag || isNaN(displayValue)) return;

    logValue(tag, displayValue);
    saveTrendPoint(tag, displayValue);
  }, [displayValue, tag, logValue]);

  // ======================================================
  // DYNAMIC UPDATE - ONLY CREATES INTERVAL ONCE
  // ======================================================
  useEffect(() => {
    // Prevent duplicate intervals in Strict Mode
    if (isMountedRef.current) {
      console.log(`⚠️ ${tag} already has interval, skipping...`);
      return;
    }

    isMountedRef.current = true;
    console.log(`✅ Create Interval ${tag}`);

    intervalRef.current = setInterval(() => {
      setDisplayValue(prev => {
        const target = targetValueRef.current;
        const diff = target - prev;
        let next = prev + diff * 0.1;
        
        if (Math.abs(diff) < 0.01) {
          next = target;
        }
        
        if (isNaN(next) || next <= 0) {
          return MinValue;
        }
        
        return next;
      });
    }, 1000);

    // ✅ CRITICAL: Cleanup on unmount
    return () => {
      console.log(`❌ Clear Interval ${tag}`);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      isMountedRef.current = false;
    };
  }, [tag, MinValue]); // ✅ Only recreate if tag changes

  // ======================================================
  // SAVE LIVE VALUE
  // ======================================================
  useEffect(() => {
    if (!isNaN(displayValue)) {
      localStorage.setItem(`transmitter_${tag}`, displayValue);
      localStorage.setItem(tag, displayValue);
    }
  }, [displayValue, tag]);

  // ======================================================
  // SAVE CONFIG
  // ======================================================
  useEffect(() => {
    if (!tag) return;

    localStorage.setItem(`min_${tag}`, min);
    localStorage.setItem(`max_${tag}`, max);
    localStorage.setItem(`HH_${tag}`, HH);
    localStorage.setItem(`H_${tag}`, H);
    localStorage.setItem(`L_${tag}`, L);
    localStorage.setItem(`LL_${tag}`, LL);
    localStorage.setItem(`description_${tag}`, description);
  }, [tag, min, max, HH, H, L, LL, description]);

  // ================= FORMAT =================
  const formattedValue = isNaN(displayValue)
    ? MinValue.toFixed(3)
    : displayValue.toFixed(3);

  // ================= RENDER =================
  return (
    <>
      <div
          className="tx-ui"
          onMouseEnter={(e) => {

            const rect = e.currentTarget.getBoundingClientRect();

            // If transmitter is close to right edge,
            // display tooltip on the left.
            setTooltipLeft(
              rect.right + 360 > window.innerWidth
            );

            setShowTooltip(true);

          }}

          onMouseLeave={() => setShowTooltip(false)}

          onClick={() => setShowFaceplate(true)}

          style={{
            position: "absolute",
            left: x,
            top: y,
            cursor: "pointer",
            fontFamily: "Arial",
            userSelect: "none",
            width: "95px",
            textAlign: "center",
          }}
        >
          {showTooltip && (
            <div
              style={{

                position: "absolute",

                top: -5,

                left: tooltipLeft ? "auto" : "100%",
                right: tooltipLeft ? "100%" : "auto",

                marginLeft: tooltipLeft ? 0 : 10,
                marginRight: tooltipLeft ? 10 : 0,

                width: "360px",

                background: "rgba(20,90,255,0.78)",

                color: "#fff",

                border: "1px solid #66ccff",

                borderRadius: "3px",

                padding: "8px 12px",

                textAlign: "left",

                pointerEvents: "none",

                whiteSpace: "normal",

                backdropFilter: "blur(4px)",

                boxShadow:
                  "0 0 8px rgba(0,180,255,0.35), 0 0 16px rgba(0,120,255,0.18)",

                zIndex: 999999,

                transition: "all .15s ease"

              }}
            >

              <div
                style={{
                  color: "#FFE95B",
                  fontWeight: "bold",
                  fontSize: "12px",
                  marginBottom: "5px",
                  borderBottom: "1px solid rgba(255,255,255,.25)",
                  paddingBottom: "3px"
                }}
              >
                {tag}
              </div>

              <div
                style={{
                  fontSize: "11px",
                  lineHeight: "16px",
                  color: "#F5F5F5"
                }}
              >
                {description}
              </div>

            </div>
          )}

        <div
          style={{
            background: tagBg,
            color: tagColor,
            padding: "2px 0px",
            fontSize: "10px",
            fontWeight: "bold",
            border: "1px solid #555",
            borderBottom: "none",
            borderTopLeftRadius: "4px",
            borderTopRightRadius: "4px",
            width: "80%",
            boxSizing: "border-box",
          }}
        >
          {tag}
        </div>

        <div
          style={{
            background: valueBg,
            color: c,
            padding: "2px 2px",
            fontSize: "11px",
            fontWeight: "bold",
            border: "1px solid #555",
            borderBottomLeftRadius: "4px",
            borderBottomRightRadius: "4px",
            width: "80%",
            boxSizing: "border-box",
          }}
        >
          {formattedValue} {unit}
        </div>
      </div>

      {showFaceplate && (
        <TransmitterFaceplate
          tag={tag}
          value={displayValue}
          unit={unit}
          min={min}
          max={max}
          HH={HH}
          H={H}
          L={L}
          LL={LL}
          description={description}
          x={x + 40}
          y={y + 40}
          onClose={() => setShowFaceplate(false)}
        />
      )}
    </>
  );
}

export default memo(Transmitter);